import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import ApplyWizard from "@/components/site/ApplyWizard";
import DemoRequestDialog from "@/components/site/DemoRequestDialog";
import PhoneInput10 from "@/components/site/PhoneInput10";
import { toE164, isValid10Digit } from "@/lib/phone";
import {
    ChartLineUp, Users, Trophy, ArrowRight, CheckCircle, Handshake, GraduationCap,
    Briefcase, MapPin, ChartBar, Stack, FileMagnifyingGlass, ShieldCheck,
    Smiley, Cube, Headset, Clock, ArrowsHorizontal, ArrowsVertical, ArrowsClockwise,
    AlignLeft, MagicWand, Triangle, Eye, Calendar,
} from "@phosphor-icons/react";

/* =========================================================================
   Anchored navigation, keeps the long page navigable
   ========================================================================= */
const NAV = [
    { id: "audiences", label: "For you" },
    { id: "why", label: "Why partner" },
    { id: "demand", label: "Patient demand" },
    { id: "treats", label: "What we treat" },
    { id: "process", label: "How it works" },
    { id: "status", label: "Application status" },
    { id: "apply", label: "Apply" },
];

/* =========================================================================
   Static content blocks
   ========================================================================= */
const PERKS = [
    {
        icon: ShieldCheck,
        title: "Medical-grade aligner material",
        desc: "Engineered thermoplastic with predictable force delivery, high optical clarity, stain resistance, and proven biocompatibility throughout treatment.",
    },
    {
        icon: Cube,
        title: "German 3D technology",
        desc: "Cases are digitised on industry-leading German 3D scanning systems and aligners are thermoformed with precision German equipment for micron-level accuracy.",
    },
    {
        icon: Headset,
        title: "24/7 COE planning support",
        desc: "Our Center of Excellence planning team is on call around the clock, clinical queries, refinements, and case escalations addressed without delays.",
    },
    {
        icon: Smiley,
        title: "Proven treatment outcomes",
        desc: "Consistent, predictable results across the full range of common malocclusions, backed by transparent case data and clinical evidence.",
    },
];

/* "Features" available on the Invisique™ platform per malocclusion */
const F = {
    attachments: { label: "Attachments", tone: "bg-sky-100 text-sky-700" },
    ipr: { label: "IPR", tone: "bg-violet-100 text-violet-700" },
    elastics: { label: "Elastics", tone: "bg-emerald-100 text-emerald-700" },
    class2: { label: "Class II mechanics", tone: "bg-amber-100 text-amber-700" },
    class3: { label: "Class III mechanics", tone: "bg-orange-100 text-orange-700" },
    expansion: { label: "Arch expansion", tone: "bg-cyan-100 text-cyan-700" },
    biteramps: { label: "Bite ramps", tone: "bg-rose-100 text-rose-700" },
    powerridges: { label: "Power ridges", tone: "bg-indigo-100 text-indigo-700" },
};

const C = {
    simple: { label: "Simple", tone: "bg-emerald-500/15 text-emerald-700 border-emerald-200" },
    moderate: { label: "Moderate", tone: "bg-amber-500/15 text-amber-700 border-amber-200" },
    complex: { label: "Complex", tone: "bg-rose-500/15 text-rose-700 border-rose-200" },
};

const TREATMENTS = [
    { id: "crowding", name: "Crowding", icon: Stack, desc: "When teeth overlap because the arch lacks space.", complexity: ["simple", "moderate", "complex"], features: ["attachments", "ipr", "expansion"] },
    { id: "spacing", name: "Spacing", icon: ArrowsHorizontal, desc: "Localised gaps between adjacent teeth.", complexity: ["simple", "moderate"], features: ["attachments", "ipr"] },
    { id: "generalised_spacing", name: "Generalised spacing", icon: AlignLeft, desc: "Diastemas distributed across the arch.", complexity: ["moderate", "complex"], features: ["attachments", "ipr", "class2"] },
    { id: "overbite", name: "Deep bite (overbite)", icon: ArrowsVertical, desc: "Upper incisors significantly overlap the lowers vertically.", complexity: ["moderate", "complex"], features: ["attachments", "biteramps", "elastics"] },
    { id: "underbite", name: "Underbite (Class III)", icon: Triangle, desc: "Lower teeth bite outside the uppers anteriorly.", complexity: ["moderate", "complex"], features: ["attachments", "class3", "ipr"] },
    { id: "crossbite", name: "Crossbite", icon: ArrowsClockwise, desc: "One or more upper teeth bite inside the lowers.", complexity: ["moderate", "complex"], features: ["attachments", "expansion", "ipr"] },
    { id: "openbite", name: "Open bite", icon: Eye, desc: "Anterior teeth do not contact when posteriors are in occlusion.", complexity: ["complex"], features: ["attachments", "biteramps", "elastics"] },
    { id: "class2", name: "Class II correction", icon: MagicWand, desc: "Distal positioning of upper relative to lower arch.", complexity: ["moderate", "complex"], features: ["class2", "elastics", "attachments"] },
    { id: "midline", name: "Midline correction", icon: AlignLeft, desc: "Asymmetry between upper and lower dental midlines.", complexity: ["simple", "moderate"], features: ["ipr", "elastics", "attachments"] },
];

const EVIDENCE = [
    { ref: "Krieger et al., J Orofac Orthop, 2012", topic: "Treatment efficacy across malocclusion types." },
    { ref: "Houle JP, Piedade L, Todescan R Jr, Pinheiro FH, Am J Orthod Dentofacial Orthop, 2017", topic: "Maxillary expansion outcomes with clear aligners." },
    { ref: "Khosravi R et al., Am J Orthod Dentofacial Orthop, 2017", topic: "Class II correction protocols and predictability." },
];

const PROCESS = [
    { n: 1, title: "Apply", desc: "Submit the form below. Our partnership team reviews within 3 business days." },
    { n: 2, title: "Onboard", desc: "Welcome call, portal access, certification, marketing assets." },
    { n: 3, title: "Submit case", desc: "5-step wizard: patient, clinical, records, prescription, review." },
    { n: 4, title: "Plan reviewed", desc: "3D treatment plan authored by you and our COE Planning team. You approve or request changes." },
    { n: 5, title: "Aligners delivered", desc: "Manufactured + shipped to your clinic with tracking, ready for fitting." },
];

/* =========================================================================
   Page
   ========================================================================= */
export default function ForDoctors() {
    const [stats, setStats] = useState(null);
    const [demoOpen, setDemoOpen] = useState(false);

    useEffect(() => {
        api.get("/provider-stats").then((r) => setStats(r.data)).catch(() => null);
    }, []);

    return (
        <div data-testid="for-doctors-page">
            <Hero onDemoOpen={() => setDemoOpen(true)} />
            <AnchorNav />
            <WhyPartner />
            <PatientDemand stats={stats} />
            <WhatWeTreat />
            <Process />
            <StatusCheck />
            <ApplySection onDemoOpen={() => setDemoOpen(true)} />
            <DemoRequestDialog open={demoOpen} onOpenChange={setDemoOpen} />
        </div>
    );
}

/* ----- Hero ----- */
function Hero({ onDemoOpen }) {
    return (
        <section className="bg-primary text-primary-foreground" id="top">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
                <div>
                    <div className="overline text-white/70 mb-4">For Dentists/Orthodontists</div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
                        Join India's premium clear aligner network.
                    </h1>
                    <p className="text-white/85 mt-5 max-w-lg">
                        Partner with Invisique™ to offer your patients cutting-edge aligner therapy backed by
                        technology, training, and patient demand routed to your clinic.
                    </p>

                    {/* Path B (2026-02-25) — two-CTA conversion ladder:
                        primary is sales-led (Request a demo → talk to a rep);
                        secondary is self-serve for already-decided applicants.
                        Provider sign-in moves to a tertiary text-link so the hero
                        doesn't fight itself with 3 equally-weighted buttons. */}
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg">
                        <Button
                            size="lg"
                            onClick={onDemoOpen}
                            className="rounded-full h-12 px-7 bg-amber-400 hover:bg-amber-300 text-foreground font-semibold shadow-lg"
                            data-testid="providers-demo-btn"
                        >
                            <Calendar size={16} weight="fill" className="mr-1.5" />
                            Request a demo
                        </Button>
                        <a href="#apply">
                            <Button
                                size="lg" variant="outline"
                                className="rounded-full h-12 px-7 bg-transparent border-white/40 text-white hover:bg-white hover:text-primary w-full sm:w-auto"
                                data-testid="providers-apply-btn"
                            >
                                Apply directly <ArrowRight size={14} className="ml-1.5" />
                            </Button>
                        </a>
                    </div>

                    <div className="mt-5 text-sm text-white/70">
                        Already a partner?{" "}
                        <button
                            onClick={() => (window.location.href = "/doctor-login")}
                            className="font-semibold text-white underline-offset-4 hover:underline"
                            data-testid="providers-signin-btn"
                        >
                            Sign in to your provider portal →
                        </button>
                    </div>
                </div>
                <div className="relative">
                    <img
                        src="/marketing/for-doctors-55.png"
                        alt="Two Invisique™ partner clinicians in a modern digital clinic, with a growth chart and intraoral scanner in the background"
                        className="rounded-3xl shadow-2xl aspect-[4/5] object-cover w-full max-h-[520px]"
                    />
                </div>
            </div>
        </section>
    );
}

/* ----- Anchored sub-nav ----- */
function AnchorNav() {
    return (
        <nav className="sticky top-16 lg:top-20 z-30 bg-card/95 backdrop-blur border-b border-border" data-testid="provider-anchor-nav">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center gap-1 sm:gap-3 h-12 overflow-x-auto">
                {NAV.map((n) => (
                    <a
                        key={n.id}
                        href={`#${n.id}`}
                        className="text-xs sm:text-sm font-medium text-foreground/70 hover:text-primary whitespace-nowrap px-2 py-1 rounded transition-colors"
                        data-testid={`anchor-${n.id}`}
                    >
                        {n.label}
                    </a>
                ))}
                <div className="ml-auto">
                    <a href="#apply">
                        <Button size="sm" className="rounded-full h-8 text-xs">Apply <ArrowRight size={12} className="ml-1" /></Button>
                    </a>
                </div>
            </div>
        </nav>
    );
}

/* ----- Why partner ----- */
function WhyPartner() {
    return (
        <Section id="why" eyebrow="Why partner" title="Clinically led. Engineered precisely. Backed end to end.">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {PERKS.map((p) => (
                    <div key={p.title} className="p-6 bg-card border border-border rounded-2xl bento-shadow" data-testid={`perk-${p.title.toLowerCase().replace(/\s/g, "-")}`}>
                        <p.icon size={28} weight="duotone" color="hsl(192 100% 31%)" />
                        <h3 className="font-display text-lg font-semibold mt-4 mb-1">{p.title}</h3>
                        <p className="text-sm text-muted-foreground">{p.desc}</p>
                    </div>
                ))}
            </div>
        </Section>
    );
}

/* ----- Patient demand (real DB stats) ----- */
function PatientDemand({ stats }) {
    return (
        <Section id="demand" eyebrow="Patient demand" title="A wider audience for your practice.">
            <p className="text-muted-foreground -mt-6 mb-8 max-w-2xl">
                Patients discover Invisique™ partner clinics through our Smile Assessment funnel, clinic directory,
                and digital marketing campaigns. Enquiries from your area surface in your portal so you can engage
                them directly, no advertising spend required.
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Users} label="Patient enquiries to date" value={stats?.leads_total} suffix="+" testid="stat-leads-total" />
                <StatCard icon={ChartBar} label="Enquiries in the last 30 days" value={stats?.leads_last_30d} suffix="" testid="stat-leads-30d" />
                <StatCard icon={MapPin} label="Cities active" value={stats?.cities} suffix="" testid="stat-cities" />
                <StatCard icon={Handshake} label="Partner clinics" value={stats?.partner_clinics} suffix="" testid="stat-partner-clinics" />
            </div>
            <p className="text-xs text-muted-foreground mt-4 italic">
                Live platform counts. Patient enquiries are distributed based on certification, proximity, and clinic availability, no guarantees of exclusivity.
            </p>
        </Section>
    );
}

function StatCard({ icon: Icon, label, value, suffix, testid }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-5 bento-shadow" data-testid={testid}>
            <Icon size={22} weight="duotone" color="hsl(192 100% 31%)" />
            <div className="text-3xl sm:text-4xl font-display font-bold mt-3 leading-none">
                {value == null ? <span className="text-muted-foreground/50">,</span> : (
                    <>{value.toLocaleString("en-IN")}<span className="text-primary">{suffix}</span></>
                )}
            </div>
            <div className="text-xs text-muted-foreground mt-1">{label}</div>
        </div>
    );
}

/* ----- What we treat ----- */
function WhatWeTreat() {
    const [filter, setFilter] = useState("all");
    const filtered = TREATMENTS.filter((t) => filter === "all" || t.complexity.includes(filter));
    return (
        <Section id="treats" eyebrow="Clinical capability" title="What Invisique™ treats.">
            <p className="text-muted-foreground -mt-6 mb-6 max-w-2xl">
                Clear aligners can address most common malocclusions. Complexity rating is indicative, the COE planning
                team can advise on borderline cases.
            </p>

            {/* Complexity filter */}
            <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="treats-filter">
                <span className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mr-1">Show:</span>
                {[
                    { id: "all", label: "All" },
                    { id: "simple", label: "Simple" },
                    { id: "moderate", label: "Moderate" },
                    { id: "complex", label: "Complex" },
                ].map((opt) => (
                    <button
                        key={opt.id}
                        onClick={() => setFilter(opt.id)}
                        data-testid={`treats-filter-${opt.id}`}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === opt.id ? "bg-primary text-white border-primary" : "border-border hover:bg-secondary"}`}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((t) => <TreatmentCard key={t.id} t={t} />)}
            </div>

            {/* Evidence */}
            <div className="mt-10 border-t border-border pt-6">
                <div className="overline mb-3 flex items-center gap-1.5"><FileMagnifyingGlass size={14} weight="duotone" className="text-primary" /> Selected peer-reviewed evidence</div>
                <ul className="space-y-1.5">
                    {EVIDENCE.map((e, i) => (
                        <li key={i} className="text-xs text-muted-foreground">
                            <span className="font-semibold text-foreground/80">{e.topic}</span>, {e.ref}
                        </li>
                    ))}
                </ul>
            </div>
        </Section>
    );
}

function TreatmentCard({ t }) {
    const Icon = t.icon;
    return (
        <div className="bg-card border border-border rounded-2xl overflow-hidden bento-shadow flex flex-col" data-testid={`treat-${t.id}`}>
            {/* Visual placeholder, replaced with real before/after photos when partner-contributed cases are available */}
            <div className="relative h-32 bg-gradient-to-br from-primary/10 via-primary/5 to-secondary flex items-center justify-center border-b border-border">
                <Icon size={42} weight="duotone" color="hsl(192 100% 31%)" />
                <span className="absolute top-2 right-2 text-[9px] uppercase tracking-wide bg-white/90 text-muted-foreground px-1.5 py-0.5 rounded font-semibold">
                    Real cases coming soon
                </span>
            </div>
            <div className="p-4 flex-1 flex flex-col">
                <h3 className="font-display text-base font-semibold">{t.name}</h3>
                <p className="text-xs text-muted-foreground mt-1 flex-1">{t.desc}</p>

                <div className="flex flex-wrap gap-1 mt-3">
                    {t.complexity.map((c) => (
                        <span key={c} className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded border font-semibold ${C[c].tone}`}>
                            {C[c].label}
                        </span>
                    ))}
                </div>

                <div className="mt-2 flex flex-wrap gap-1">
                    {t.features.map((f) => (
                        <span key={f} className={`text-[10px] px-1.5 py-0.5 rounded-full ${F[f].tone}`}>
                            {F[f].label}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ----- How it works ----- */
function Process() {
    return (
        <Section id="process" eyebrow="How it works" title="From application to first case." dark>
            <ol className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-6">
                {PROCESS.map((p) => (
                    <li key={p.n} className="bg-white/10 border border-white/20 rounded-2xl p-5 backdrop-blur" data-testid={`process-${p.n}`}>
                        <div className="w-8 h-8 rounded-full bg-white text-primary font-bold flex items-center justify-center mb-3">
                            {p.n}
                        </div>
                        <h3 className="font-display text-base font-semibold mb-1">{p.title}</h3>
                        <p className="text-xs text-white/80">{p.desc}</p>
                    </li>
                ))}
            </ol>
        </Section>
    );
}

/* ----- Status check ----- */
function StatusCheck() {
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    const check = async (e) => {
        e.preventDefault();
        const cleanEmail = email.trim();
        const hasEmail = !!cleanEmail;
        const hasPhone = isValid10Digit(phone);
        if (!hasEmail && !hasPhone) {
            return toast.error("Enter the email or 10-digit phone you applied with");
        }
        setBusy(true);
        setResult(null);
        // Build the payload. Phone goes as E.164 because applications
        // are stored in E.164 — sending the bare 10 digits would miss
        // every record (exact-string match on the backend).
        const payload = {};
        if (hasEmail) payload.email = cleanEmail;
        if (hasPhone) payload.phone = toE164(phone);
        try {
            const r = await api.post("/provider-apply/status", payload);
            setResult(r.data);
        } catch {
            toast.error("Couldn't check status. Please try again.");
        }
        setBusy(false);
    };

    const STATUS_LABEL = {
        pending: { label: "Under review", tone: "bg-amber-100 text-amber-700 border-amber-300", icon: Clock },
        in_review: { label: "In review", tone: "bg-amber-100 text-amber-700 border-amber-300", icon: Clock },
        docs_requested: { label: "Documents requested", tone: "bg-amber-100 text-amber-700 border-amber-300", icon: Clock },
        approved: { label: "Approved, welcome!", tone: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: CheckCircle },
        rejected: { label: "Not approved this round", tone: "bg-rose-100 text-rose-700 border-rose-300", icon: ShieldCheck },
    };

    return (
        <Section id="status" eyebrow="Already applied?" title="Check your application status.">
            <p className="text-muted-foreground -mt-6 mb-6 max-w-2xl">
                Enter the same email or phone you used when applying. We&apos;ll show you where your application currently sits.
            </p>
            <form onSubmit={check} className="max-w-xl space-y-3" data-testid="status-form">
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <Label htmlFor="status-email" className="text-xs font-semibold">Email</Label>
                        <Input
                            id="status-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@clinic.com"
                            className="h-12 mt-1.5"
                            data-testid="status-email-input"
                        />
                    </div>
                    <div>
                        <Label htmlFor="status-phone" className="text-xs font-semibold">Phone</Label>
                        <div className="h-12 mt-1.5">
                            <PhoneInput10
                                id="status-phone"
                                value={phone}
                                onChange={setPhone}
                                inputClassName="h-12"
                                testid="status-phone-input"
                            />
                        </div>
                    </div>
                </div>
                <p className="text-[11px] text-muted-foreground">Either field is enough — fill the one you applied with.</p>
                <Button type="submit" disabled={busy} className="h-12 rounded-full px-6" data-testid="status-submit">
                    {busy ? "Checking…" : "Check status"}
                </Button>
            </form>

            {result && (
                <div className="mt-5 max-w-xl" data-testid="status-result">
                    {!result.found ? (
                        <div className="border border-border rounded-xl p-4 bg-secondary/30 text-sm">
                            No application found with those details. Maybe you used a different email or phone?
                            Or <a href="#apply" className="text-primary font-semibold">apply now</a>.
                        </div>
                    ) : (
                        <div className={`border rounded-xl p-4 ${(STATUS_LABEL[result.status] || STATUS_LABEL.pending).tone}`}>
                            <div className="flex items-center gap-2">
                                {React.createElement((STATUS_LABEL[result.status] || STATUS_LABEL.pending).icon, { size: 18, weight: "fill" })}
                                <span className="font-semibold text-sm">
                                    {(STATUS_LABEL[result.status] || STATUS_LABEL.pending).label}
                                </span>
                            </div>
                            <div className="text-xs mt-1.5 opacity-90">
                                <strong>{result.clinic}</strong> · {result.name}
                            </div>
                            <div className="text-[11px] mt-0.5 opacity-75">
                                Submitted {new Date(result.submitted_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Section>
    );
}

/* ----- Apply section (replaces legacy flat ApplyForm with the 3-step
       ApplyWizard). Path B Phase 2 — 2026-02-25. Keeps the #apply anchor live
       so existing deep-links + the Hero's "Apply directly" CTA still scroll
       here. */
function ApplySection({ onDemoOpen }) {
    return (
        <section id="apply" className="max-w-3xl mx-auto px-5 sm:px-8 pb-20 pt-12 scroll-mt-32" data-testid="apply-section">
            <div className="bg-card border border-border rounded-3xl p-6 lg:p-10 bento-shadow">
                <div className="mb-7">
                    <div className="overline text-primary mb-2 flex items-center gap-1.5">
                        <Briefcase size={11} weight="fill" /> Apply to join
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                        Self-serve application. <span className="text-primary">Three quick steps.</span>
                    </h2>
                    <p className="text-sm text-muted-foreground mt-2.5 max-w-xl">
                        Already decided? Submit your credentials directly. Want a call first?{" "}
                        <button
                            onClick={onDemoOpen}
                            className="text-primary font-semibold hover:underline"
                            data-testid="apply-section-demo-link"
                        >
                            Request a demo instead →
                        </button>
                    </p>
                </div>
                <ApplyWizard />
            </div>
        </section>
    );
}

/* ----- Helpers ----- */
function Section({ id, eyebrow, title, dark, children }) {
    const wrapClass = dark
        ? "bg-primary text-primary-foreground"
        : "bg-background";
    return (
        <section id={id} className={`${wrapClass} scroll-mt-32 py-16 lg:py-20`}>
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="max-w-2xl mb-10">
                    <div className={`overline mb-3 ${dark ? "text-white/70" : "text-primary"}`}>{eyebrow}</div>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{title}</h2>
                </div>
                {children}
            </div>
        </section>
    );
}
